// JSXList.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { ListProvider } from './ListContext';
import { VirtualList } from './VirtualList';
import { useQwanyx, Card } from "qwanyx"; // Import useQwanyx hook

// Create an item context to make the current item available to children
const ItemContext = createContext(null);

// Hook to access the current item
export const useListItem = () => {
    const item = useContext(ItemContext);
    if (item === undefined) {
        throw new Error('useListItem must be used within a JSXList component');
    }
    return item;
};

// Wrapper component for JSX-based list items
export const ListItemWrapper = ({ children }) => {
    const item = useListItem();
    return children(item);
};

/**
 * JSXList - A component for rendering lists with JSX
 * @param {Object} props
 * @param {Object} props.query - MongoDB query
 * @param {Object} props.sort - Sort options
 * @param {Object} props.projection - Field projection
 * @param {number} props.batchSize - Number of items to load at once
 * @param {number} props.threshold - Threshold for loading more items
 * @param {boolean} props.loadAllAtOnce - Whether to load all items at once
 * @param {Function} props.renderItem - Function that renders an item (item) => JSX
 * @param {Function} props.renderDetail - Function that renders the detail view (item) => JSX
 * @param {React.ReactNode} props.children - Can be functions: first for items, second for detail, third for search
 * @param {string} props.width - Width of the list container
 * @param {string} props.height - Height of the list container
 * @param {string} props.className - CSS class for the list container
 * @param {Object} props.style - Additional styles for the list container
 * @param {string} props.di - The display identifier for the list
 * @param {string} props.pa - The pa property for all items in the list
 * @param {boolean} props.clickable - Whether items are clickable (default: true)
 * @param {boolean} props.searchable - Whether to show search box (default: false)
 * @param {string} props.searchField - Field to search in (default: 'title')
 * @param {string} props.searchPlaceholder - Placeholder text for search box
 * @param {Object} props.searchStyles - Custom styles for the search box
 * @param {Object} props.buttonStyles - Custom styles for the search button
 */
export function JSXList({
    query = {},
    sort = {},
    projection = {},
    batchSize = 20,
    threshold = 300,
    loadAllAtOnce = true,
    renderItem,
    renderDetail,
    children,
    width = "100%",
    height = "100%",
    className = "",
    style = {},
    di,
    pa,
    clickable = true,
    searchable = false,
    searchField = 'title',
    searchPlaceholder = 'Search...',
    searchStyles = {},
    buttonStyles = {},
}) {
    // State for the selected item
    const [selectedItem, setSelectedItem] = useState(null);
    // State for the search input value
    const [searchInput, setSearchInput] = useState('');
    // State for the active search term (after button click)
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    // State for the combined query
    const [combinedQuery, setCombinedQuery] = useState(query);
    // Key to force ListProvider to re-create when query changes
    const [listKey, setListKey] = useState(0);

    // Determine the item, detail, and search renderers based on what's provided
    let itemRenderer = renderItem;
    let detailRenderer = renderDetail;
    let searchRenderer = null;

    // If children are provided instead of renderItem/renderDetail functions
    if (children) {
        // Check if children is an array with functions
        if (Array.isArray(children)) {
            // First function is for rendering items
            if (typeof children[0] === 'function') {
                itemRenderer = children[0];
            }

            // Second function is for rendering detail
            if (children.length >= 2 && typeof children[1] === 'function') {
                detailRenderer = children[1];
            }

            // Third function (if provided) is for rendering search
            if (children.length >= 3 && typeof children[2] === 'function') {
                searchRenderer = children[2];
            }
        }
        // If children is a single function, use it for rendering items
        else if (typeof children === 'function') {
            itemRenderer = children;
        }
    }

    const containerStyle = {
        width,
        height,
        overflowY: 'auto',
        ...style
    };

    // Default search box styles
    const defaultSearchStyles = {
        width: '100%',
        padding: '10px',
        marginBottom: '0',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxSizing: 'border-box',
    };

    const mergedSearchStyles = {
        ...defaultSearchStyles,
        ...searchStyles
    };

    // Effect to update the combined query when active search term changes
    // In JSXList.js, modify the useEffect for search

    // Effect to update the combined query when active search term changes
    useEffect(() => {
        // Skip if there's no active search term
        if (!activeSearchTerm.trim()) {
            // For empty search, set the base query with a placeholder that won't match anything
            if (Array.isArray(query) && query.length > 0 && query[0].$search) {
                // Create a copy of the Atlas Search query
                const emptySearchQuery = [...query];

                // Replace text queries with a placeholder that won't match anything
                if (emptySearchQuery[0].$search.compound && emptySearchQuery[0].$search.compound.must) {
                    // Find and replace any existing text queries (keep only exists condition)
                    emptySearchQuery[0].$search.compound.must = emptySearchQuery[0].$search.compound.must.filter(
                        condition => condition.exists
                    );

                    // Add placeholder text query
                    emptySearchQuery[0].$search.compound.must.unshift({
                        text: {
                            query: "EMPTY_SEARCH_PLACEHOLDER_XYZ",
                            path: { wildcard: "*" }
                        }
                    });
                }

                setCombinedQuery(emptySearchQuery);
            } else {
                // For regular queries, just use the original query
                setCombinedQuery(query);
            }

            setListKey(prevKey => prevKey + 1);
            return;
        }

        // Process the search terms
        const searchTerms = activeSearchTerm.trim().split(/\s+/);

        if (Array.isArray(query) && query.length > 0 && query[0].$search) {
            // This is an Atlas Search query
            const searchQuery = [...query];

            // Extract the exists condition if present
            let existsCondition = null;
            if (searchQuery[0].$search.compound && searchQuery[0].$search.compound.must) {
                searchQuery[0].$search.compound.must.forEach(condition => {
                    if (condition.exists) {
                        existsCondition = condition;
                    }
                });
            }

            // Create a new must array with a text query for each search term
            const mustConditions = [];

            // Add a text search for each term
            searchTerms.forEach(term => {
                mustConditions.push({
                    text: {
                        query: term,
                        path: { wildcard: "*" }
                    }
                });
            });

            // Add back the exists condition if it was present
            if (existsCondition) {
                mustConditions.push(existsCondition);
            }

            // Update the search query
            searchQuery[0].$search.compound.must = mustConditions;

            setCombinedQuery(searchQuery);
        } else {
            // Regular MongoDB query (not Atlas Search)
            // Create AND conditions for each search term
            const regexConditions = searchTerms.map(term => ({
                [searchField]: { $regex: term, $options: 'i' }
            }));

            setCombinedQuery({
                ...query,
                $and: regexConditions
            });
        }

        // Force refresh of the list
        setListKey(prevKey => prevKey + 1);
    }, [query, activeSearchTerm, searchField]);

    // Handle search input change
    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    // Handle search button click
    const handleSearchSubmit = (e) => {
        e.preventDefault(); // Prevent form submission
        setActiveSearchTerm(searchInput);
        console.log("Search submitted for:", searchInput);
    };

    // Handle click on an item
    const handleItemClick = (item) => {
        if (!item || !clickable) return;
        console.log("Item clicked:", item);
        setSelectedItem(item);
    };

    // Handle going back to the list
    const handleBackToList = () => {
        setSelectedItem(null);
    };

    // Internal render function that just handles the rendering without click wrapper
    const internalRenderItem = (item, index) => {
        // If itemRenderer is provided, use it
        if (itemRenderer && typeof itemRenderer === 'function') {
            return itemRenderer(item, index);
        }

        // Otherwise, use default rendering
        return (
            <div style={{ padding: '10px', border: '1px solid #ddd', marginBottom: '5px' }}>
                <strong>ID:</strong> {item._id}
                {item[searchField] && <div><strong>{searchField}:</strong> {item[searchField]}</div>}
            </div>
        );
    };

    // Render function for the detail view
    const renderDetailView = (item) => {
        // If detailRenderer is provided, use it
        if (detailRenderer && typeof detailRenderer === 'function') {
            return (
                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '15px', textAlign: 'right' }}>
                        <button
                            onClick={handleBackToList}
		            className='mdi mdi-window-close'
                            style={{
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        ></button>
                    </div>
                    {detailRenderer(item)}
                </div>
            );
        }

        // Otherwise, use default detail rendering
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                    <button
                        onClick={handleBackToList}
                        style={{
                            padding: '8px 15px',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        ← Back to List
                    </button>
                </div>

                <div style={{
                    padding: '20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9'
                }}>
                    <h3>Selected Item Details</h3>
                    <div>
                        <strong>ID:</strong> {item._id}
                    </div>
                    <div>
                        <strong>Display ID:</strong> {item.di}
                    </div>
                    {item[searchField] &&
                        <div>
                            <strong>{searchField}:</strong> {item[searchField]}
                        </div>}

                    {/* Render Card component */}
                    <div style={{ marginTop: '20px' }}>
                        <Card
                            _id={item._id}
                            di={item.di}
                            pa={pa}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // Create search props object to pass to custom render function
    const searchProps = {
        searchInput,
        setSearchInput,
        handleSearchInputChange,
        handleSearchSubmit,
        searchField,
        searchPlaceholder,
        searchStyles: mergedSearchStyles,
        buttonStyles,
        setActiveSearchTerm
    };

    // Default search component
    const defaultRenderSearch = () => (
        <div style={{ display: 'flex', marginBottom: '15px' }}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', width: '100%' }}>
                <input
                    type="text"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    placeholder={searchPlaceholder}
                    style={{
                        ...mergedSearchStyles,
                        marginBottom: 0,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    }}
                />
                <button
                    type="submit"
                    style={{
                        padding: '10px 15px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderTopRightRadius: '4px',
                        borderBottomRightRadius: '4px',
                        cursor: 'pointer',
                        ...buttonStyles
                    }}
                >
                    Search
                </button>
            </form>
        </div>
    );

    return (
        <ListProvider
            key={listKey} // Add key to force re-render when query changes
            query={combinedQuery}
            sort={sort}
            projection={projection}
            batchSize={batchSize}
            autoLoadThreshold={threshold}
            loadAllAtOnce={loadAllAtOnce}
            initialItems={[]} // Reset items when query changes
        >
            {/* Detail view - only shown when an item is selected */}
            {selectedItem && renderDetailView(selectedItem)}

            {/* List view - always in the DOM, but hidden when an item is selected */}
            <div
                className={className}
                style={{
                    ...containerStyle,
                    display: selectedItem ? 'none' : 'block'
                }}
            >
                {/* Search box */}
                {searchable && (
                    searchRenderer
                        ? searchRenderer(searchProps)
                        : defaultRenderSearch()
                )}

                <VirtualList
                    renderItem={internalRenderItem}
                    onItemClick={handleItemClick}
                    clickable={clickable}
                />
            </div>
        </ListProvider>
    );
}

// Re-export the hook for convenience
JSXList.useItem = useListItem;
JSXList.Item = ListItemWrapper;

export default JSXList;
